/**
 * Pure helpers for the publish-view starter coachmarks.
 *
 * One tip per template: a kid opening the Publish view on a given
 * template for the first time gets a single anchored card pointing
 * at the part most worth knowing. Dismissal is sticky per template,
 * so the kid never sees the same tip twice. Reset via
 * `resetPublishCoachmarks()` in dev / for a teacher demo.
 *
 * State lives in localStorage under its own key so it can't collide
 * with the studio's main coachmark flow.
 */

import type { TemplateId } from './types';

const STORAGE_KEY = 'bryta-publish-coachmarks-v1';

export function readSeenTemplateTips(): Set<TemplateId> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as { seen?: TemplateId[] };
    return new Set(parsed.seen ?? []);
  } catch {
    return new Set();
  }
}

export function writeSeenTemplateTips(set: Set<TemplateId>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ seen: Array.from(set) }));
  } catch {
    // localStorage can fail in private mode; tip just reappears.
  }
}

/** Reset all publish-view tips so a teacher demoing or a kid wanting
 *  the full intro gets it again. */
export function resetPublishCoachmarks(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
